import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Lightbulb, Loader2, Upload, Trash2, ArrowRight, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/thesis`;

/** Upload your own papers → InkPhD proposes the next projects, novelty-checked against PubMed. */
export default function AuthorIdeasPanel({ userId }) {
  const navigate = useNavigate();
  const [pubs, setPubs] = useState([]);
  const [ideas, setIdeas] = useState(null);
  const [focus, setFocus] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/author-publications`, { params: { user_id: userId } });
      setPubs(data.publications || []);
      setIdeas(data.ideas || null);
    } catch { /* silent */ }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setBusy('upload');
    const form = new FormData();
    form.append('file', file);
    try {
      await axios.post(`${API}/author-publications`, form,
        { params: { user_id: userId }, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Publication added');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not read that file');
    } finally {
      setBusy('');
    }
  };

  const generate = async () => {
    setBusy('ideas');
    try {
      const { data } = await axios.post(`${API}/author-ideas`,
        { focus: focus || null, use_pubmed: true }, { params: { user_id: userId } });
      setIdeas(data);
      toast.success(`${data.ideas?.length || 0} ideas generated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate ideas');
    } finally {
      setBusy('');
    }
  };

  const startProject = async (index) => {
    setBusy(`idea-${index}`);
    try {
      const { data } = await axios.post(`${API}/author-ideas/to-project`, { index },
        { params: { user_id: userId } });
      toast.success('Project created');
      navigate(`/thesis/${data.project_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || e?.response?.data?.detail || 'Could not create the project');
    } finally {
      setBusy('');
    }
  };

  return (
    <Card data-testid="author-ideas-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-amber-500" /> Ideas from your own work
        </CardTitle>
        <CardDescription>
          Add your published papers. InkPhD builds your topical profile, checks recent PubMed
          literature for what is already covered, and proposes the next projects you are
          positioned to write.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex">
            <input type="file" accept=".pdf,.docx,.txt,.md" className="hidden"
              data-testid="ideas-upload-input" onChange={(e) => upload(e.target.files?.[0])} />
            <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold rounded-full
              border border-border px-3.5 py-1.5 cursor-pointer hover:border-[#1d4ed8]/50 transition-colors">
              {busy === 'upload' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              Add a publication
            </span>
          </label>
          <span className="text-[12px] text-muted-foreground" data-testid="ideas-pub-count">
            {pubs.length} publication{pubs.length === 1 ? '' : 's'} in your profile
          </span>
        </div>

        {pubs.length > 0 && (
          <ul className="divide-y divide-border" data-testid="ideas-pub-list">
            {pubs.map((p) => (
              <li key={p.id} className="py-2 flex items-center gap-3 text-[13px]">
                <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="flex-1 min-w-0 truncate" title={p.title}>{p.title}</span>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600"
                  data-testid={`ideas-pub-delete-${p.id}`}
                  onClick={async () => {
                    await axios.delete(`${API}/author-publications/${p.id}`, { params: { user_id: userId } });
                    load();
                  }}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap gap-2 items-center">
          <Input value={focus} onChange={(e) => setFocus(e.target.value)}
            placeholder="Optional focus, e.g. 'pancreatic surgery outcomes'"
            className="h-9 max-w-xs" data-testid="ideas-focus-input" />
          <Button onClick={generate} disabled={!pubs.length || busy === 'ideas'}
            data-testid="ideas-generate-btn"
            className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white rounded-full">
            {busy === 'ideas' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lightbulb className="w-4 h-4 mr-2" />}
            {ideas ? 'Regenerate ideas' : 'Generate ideas'}
          </Button>
          <span className="text-[11px] text-muted-foreground">
            Uses PubMed for novelty · counts as one generation · ~40 s
          </span>
        </div>

        {ideas?.topical_profile && (
          <div className="rounded-lg border border-[#1d4ed8]/30 bg-[#1d4ed8]/[0.05] p-3"
            data-testid="ideas-profile">
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
              Your research identity
            </p>
            <p className="text-[13px] leading-relaxed">{ideas.topical_profile}</p>
          </div>
        )}

        {ideas?.ideas?.length > 0 && (
          <ul className="space-y-3" data-testid="ideas-list">
            {ideas.ideas.map((idea, i) => (
              <li key={i} className="rounded-lg border border-border p-4" data-testid={`idea-${i}`}>
                <div className="flex flex-wrap items-center gap-2 mb-1.5">
                  <Badge variant="outline" className="text-[10px] uppercase">
                    {(idea.project_type || 'research_article').replace(/_/g, ' ')}
                  </Badge>
                  {idea.effort && (
                    <Badge variant="outline" className="text-[10px]">{idea.effort} effort</Badge>
                  )}
                </div>
                <p className="text-sm font-semibold leading-snug">{idea.title}</p>
                {idea.research_question && (
                  <p className="text-[12.5px] text-muted-foreground mt-1">{idea.research_question}</p>
                )}
                {idea.novelty_note && (
                  <p className="text-[12px] mt-2"><strong>Novelty:</strong> {idea.novelty_note}</p>
                )}
                {idea.why_now && (
                  <p className="text-[12px] mt-1"><strong>Why now:</strong> {idea.why_now}</p>
                )}
                {idea.builds_on?.length > 0 && (
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Builds on: {idea.builds_on.join(' · ')}
                  </p>
                )}
                <Button size="sm" variant="outline" className="rounded-full mt-3"
                  disabled={busy === `idea-${i}`} onClick={() => startProject(i)}
                  data-testid={`idea-start-${i}`}>
                  {busy === `idea-${i}` ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                  Start this project <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
